import type { ReactElement } from 'react';
import { useStore } from '@nanostores/react';
import { Box, PageLoader, Panel } from '@customerio/pluma-components/react';
import { sessionsStore } from '@customerio/hydra-session-state';
import { session } from '@/services/session';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { SignupHeader } from './components/signup-header';
import { StepTransition } from './components/step-transition';
import { SignupFlowProvider, useSignupFlow } from './signup-flow';
import { SignupAnalyticsEvent, signupTypeProps } from './utils/analytics';
import type { SignupPageProps } from './types';
import type { SignupStep } from './utils/steps';
import { IndexStep } from './steps/index-step';
import { PasswordStep } from './steps/password-step';
import { CompanyStep } from './steps/company-step';
import { ConfirmationStep } from './steps/confirmation-step';
import { OnboardingUsageStep } from './steps/onboarding-usage-step';
import { OnboardingPersonalizeStep } from './steps/onboarding-personalize-step';
import { ChannelsStep } from './steps/channels-step';
import { PlanChoiceStep } from './steps/plan-choice-step';
import { StartupCompleteStep } from './steps/startup-complete-step';
import * as styles from './index.css';

// A total Record keyed on SignupStep: adding a step without a view is a
// compile error, so this stays exhaustive as steps gain real components.
const STEP_VIEWS: Record<SignupStep, () => ReactElement> = {
  index: IndexStep,
  password: PasswordStep,
  company: CompanyStep,
  confirmation: ConfirmationStep,
  onboarding_usage: OnboardingUsageStep,
  onboarding_personalize: OnboardingPersonalizeStep,
  channels: ChannelsStep,
  plan_choice: PlanChoiceStep,
  startup_complete: StartupCompleteStep,
};

type SignupStepViewProps = {
  step: SignupStep;
};

function SignupStepView({ step }: SignupStepViewProps) {
  const StepView = STEP_VIEWS[step];
  return <StepView />;
}

/**
 * The funnel shell: dark logo header plus the current step in a centered
 * panel. The flow-dependent variants — the startup two-column split, the
 * wider modern stage, the promo banner above the panel, dark theming, and the
 * terms-of-service notice — land with the flow state and their steps, each
 * branching exactly where its layout diverges here.
 */
function SignupContent({ step }: Pick<SignupPageProps, 'step'>) {
  const { effectiveSignupType, trackSignupEvent } = useSignupFlow();

  return (
    <Box className={styles.signupSurface} style={{ minHeight: '100vh' }}>
      <SignupHeader
        showLoginLink={step === 'index'}
        onLoginLinkClick={() =>
          trackSignupEvent(SignupAnalyticsEvent.SignInClicked, signupTypeProps({ signupType: effectiveSignupType }))
        }
      />
      <Box
        as="main"
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        pb="500"
        pt="600"
        px="300"
        style={{ minHeight: 'calc(100vh - 40px)' }}
      >
        <Box w="full" style={{ maxWidth: '430px' }}>
          <StepTransition stepKey={step}>
            <Panel mb="300" w="full">
              <SignupStepView step={step} />
            </Panel>
          </StepTransition>
        </Box>
      </Box>
    </Box>
  );
}

export default function SignupPage(props: SignupPageProps) {
  const isAuthenticated = useStore(session.isAuthenticated);
  const isInitialized = useStore(session.isInitialized);
  const hasPersistedSession = useStore(sessionsStore).length > 0;

  // While a persisted session exists but the `/accounts/current` bootstrap
  // hasn't resolved, `isAuthenticated` is still `false` — we don't yet know
  // whether to redirect. Show a loader instead of flashing the form. A visitor
  // with no persisted session is definitely logged out, so the form renders
  // immediately.
  if (hasPersistedSession && !isInitialized) {
    return <PageLoader />;
  }

  // Already-authenticated users don't belong on the signup funnel — send them
  // to the app. Unconditional across every step: the funnel runs entirely
  // unauthenticated and only authenticates at its very end (the flow
  // provider's end-of-sequence session flip lands here and redirects).
  if (isAuthenticated) {
    return <SafeNavigate to="/" options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <SignupFlowProvider
      step={props.step}
      onNavigateStep={props.onNavigateStep}
      signupType={props.signupType}
      promoCode={props.promoCode}
      initialFormValues={props.initialFormValues}
    >
      <SignupContent step={props.step} />
    </SignupFlowProvider>
  );
}
